import { Button, Container, Divider, Group, SplitScreen, Tabs, Text, useFormSubmissionController } from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';
import { useState } from 'react';

import { Header } from '../../../layouts';
import { IssueInvoiceForm, IssueInvoiceFormProps, PreviewTabs } from '../components';
import { CreateInvoiceFormFields, SubmitTarget, TabsNames } from '../types';
import { useScreenLabels } from './useScreenLabels';

export type IssueInvoiceScreenProps = {
  error?: PlatformError;
  onClose: VoidFunction;
  onBack?: VoidFunction;
  onSubmit: (
    data: CreateInvoiceFormFields,
    event?: React.MouseEvent<HTMLButtonElement>,
    target?: SubmitTarget
  ) => unknown;
  onUploadCompanyLogo?: VoidFunction;
  isLoading: boolean;
  invoiceId: string;
  isSubmitting: boolean;
  submitTarget?: SubmitTarget;
  isDraft?: boolean;
  defaultValues?: IssueInvoiceFormProps['defaultValues'];
  defaultTab?: TabsNames;
  shouldDisplayUploadCompanyLogoBanner: boolean;
};

export const IssueInvoiceScreen = forwardRef<IssueInvoiceScreenProps>(
  (
    {
      invoiceId,
      defaultValues,
      isLoading,
      isSubmitting,
      onClose,
      onBack,
      onSubmit,
      onUploadCompanyLogo,
      submitTarget,
      error,
      isDraft,
      defaultTab = 'PDF',
      shouldDisplayUploadCompanyLogoBanner,
      ...props
    },
    ref
  ) => {
    const [isSelectSendEmail, setIsSelectSendEmail] = useState(true);
    const [activeTab, setActiveTab] = useState<TabsNames>(defaultTab);
    const { onSubmissionStateChange, handleSubmit } = useFormSubmissionController<CreateInvoiceFormFields>();

    const { headerText, tabs } = useScreenLabels({ isDraft });

    return (
      <SplitScreen
        data-component={IssueInvoiceScreen.displayName}
        header={<Header onBack={isDraft ? undefined : onBack} onClose={onClose} />}
        panelA={{
          header: (
            <Container paddingLeft="xxl">
              <Tabs
                variant="default"
                ref={ref}
                activeTab={activeTab}
                onChange={(tab) => setActiveTab(tab as TabsNames)}
                tabs={tabs}
                data-testid="preview-tabs"
              />
            </Container>
          ),
          content: (
            <PreviewTabs
              activeTab={activeTab}
              invoiceId={invoiceId}
              onUploadCompanyLogo={onUploadCompanyLogo}
              shouldDisplayUploadCompanyLogoBanner={shouldDisplayUploadCompanyLogoBanner}
            />
          ),
        }}
        panelB={{
          footer: (
            <IssueInvoiceFooter
              shouldSend={isSelectSendEmail}
              isDraft={isDraft}
              isLoading={isLoading}
              isSubmitting={isSubmitting}
              handleSubmit={handleSubmit}
              submitTarget={submitTarget}
            />
          ),
          header: (
            <Container paddingX="xl" paddingY="m">
              <Text textStyle="heading1Semi">{headerText}</Text>
            </Container>
          ),
          content: (
            <IssueInvoiceForm
              onSubmissionStateChange={onSubmissionStateChange}
              onSelectSendEmail={setIsSelectSendEmail}
              onSubmit={onSubmit}
              defaultValues={defaultValues}
              {...props}
            />
          ),
        }}
        variant="2:1"
        isLoading={isLoading}
        data-testid="issue-invoice-screen"
        ref={ref}
      />
    );
  }
);

IssueInvoiceScreen.displayName = 'InvoiceScreen';

type FooterProps = {
  isLoading: boolean;
  isSubmitting: boolean;
  handleSubmit?: (target?: SubmitTarget) => VoidFunction;
  submitTarget?: SubmitTarget;
  isDraft?: boolean;
  shouldSend?: boolean;
};

const IssueInvoiceFooter = ({
  isLoading,
  shouldSend = false,
  isSubmitting,
  handleSubmit,
  submitTarget,
  isDraft,
}: FooterProps) => {
  const { primaryButtonLabel, secondaryButtonLabel } = useScreenLabels({ isDraft, shouldSend });

  return (
    <Group variant="vertical" width="full" data-component={IssueInvoiceFooter.displayName}>
      <Divider />
      <Container paddingX="m" paddingY="s">
        <Group spacing="m" justifyContent="flex-end">
          <Button
            onClick={handleSubmit?.('secondary')}
            isLoading={isLoading || (isSubmitting && submitTarget === 'secondary')}
            isDisabled={isSubmitting && submitTarget !== 'secondary'}
            label={secondaryButtonLabel}
            data-testid="create-and-close-link-button"
            variant="secondary"
          />
          <Button
            onClick={handleSubmit?.('primary')}
            isLoading={isLoading || (isSubmitting && submitTarget === 'primary')}
            isDisabled={isSubmitting && submitTarget !== 'primary'}
            label={primaryButtonLabel}
            data-testid="create-and-send-link-button"
          />
        </Group>
      </Container>
    </Group>
  );
};

IssueInvoiceFooter.displayName = 'IssueInvoiceFooter';
